import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { trackEvent, waLink } from "../lib/api";

const SOLUTIONS = [
  { label: "Hospital Setup & Turnkey Solutions", to: "/solutions#hospital-setup" },
  { label: "Medical Equipment & Devices", to: "/solutions#equipment" },
  { label: "Dialysis Centre Solutions", to: "/solutions#dialysis" },
  { label: "OT & Critical Care", to: "/solutions#ot-critical-care" },
  { label: "Diagnostic & Laboratory Solutions", to: "/solutions#diagnostic" },
  { label: "Hospital Furniture", to: "/solutions#furniture" },
];

const SERVICES = [
  { label: "Complete Hospital Setup", to: "/hospital-setup" },
  { label: "Healthcare Financing Support", to: "/services#financing" },
  { label: "Licensing & Accreditation Support", to: "/services#licensing" },
  { label: "After-Sales Service", to: "/services#after-sales" },
];

const NAV = [
  { label: "About Us", to: "/about" },
  { label: "Healthcare Solutions", to: "/solutions", menu: SOLUTIONS },
  { label: "Products", to: "/products" },
  { label: "Projects", to: "/projects" },
  { label: "Services", to: "/services", menu: SERVICES },
  { label: "Industries We Serve", to: "/industries" },
  { label: "Resources", to: "/resources" },
  { label: "Contact Us", to: "/contact" },
];

const Dropdown = ({ item }) => (
  <div className="group relative">
    <Link
      to={item.to}
      className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium text-slate-700 transition-colors hover:text-brand-700 xl:px-3"
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {item.label}
      <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform group-hover:rotate-180" />
    </Link>
    <div className="invisible absolute left-0 top-full z-50 w-80 translate-y-1 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white/95 py-2 shadow-[0_24px_60px_rgba(11,46,56,0.14)] backdrop-blur-xl">
        {item.menu.map((sub) => (
          <Link
            key={sub.label}
            to={sub.to}
            className="flex items-center justify-between px-5 py-2.5 text-sm text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            {sub.label}
            <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity hover:opacity-60" />
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const MobileNav = ({ open, close }) => (
  <div
    className={`fixed inset-0 z-40 bg-brand-950 transition-opacity duration-300 lg:hidden ${
      open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
    }`}
  >
    <div className="h-full overflow-y-auto px-6 pb-24 pt-24">
      {NAV.map((item) => (
        <div key={item.label} className="border-b border-white/10 py-1">
          <Link
            to={item.to}
            onClick={close}
            className="block py-3.5 font-heading text-2xl font-semibold text-white"
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {item.label}
          </Link>
          {item.menu && (
            <div className="pb-3 pl-1">
              {item.menu.map((sub) => (
                <Link
                  key={sub.label}
                  to={sub.to}
                  onClick={close}
                  className="block py-2 text-sm text-white/60"
                >
                  {sub.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="mt-8 flex flex-col gap-3">
        <Link to="/contact" onClick={close} className="btn-accent w-full" data-testid="mobile-cta-consultation">
          Request a Consultation
        </Link>
        <Link to="/contact?intent=quote" onClick={close} className="btn-ghost-light w-full" data-testid="mobile-cta-quote">
          Request a Quote
        </Link>
        <a href={waLink()} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { source: "mobile_nav" })} className="btn-ghost-light w-full">
          WhatsApp Us
        </a>
      </div>
    </div>
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b border-white/40 bg-white/80 shadow-[0_8px_30px_rgba(11,46,56,0.06)] backdrop-blur-xl" : "bg-white/70 backdrop-blur-md"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[76px] w-full max-w-[1440px] items-center justify-between gap-4 px-5 md:px-8 lg:gap-3">
          <Link to="/" data-testid="nav-logo" aria-label="AccuSurge home">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-0.5 lg:flex xl:gap-1" aria-label="Primary">
            {NAV.map((item) =>
              item.menu ? (
                <Dropdown key={item.label} item={item} />
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium transition-colors hover:text-brand-700 xl:px-3 ${
                    pathname.startsWith(item.to) ? "text-brand-700" : "text-slate-700"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <Link to="/contact?intent=quote" className="btn-outline !h-10 !px-4 text-[13px]" data-testid="header-quote-btn">
              Request a Quote
            </Link>
            <Link to="/contact" className="btn-accent !h-10 !px-4 text-[13px]" data-testid="header-consultation-btn">
              Request a Consultation
            </Link>
          </div>
          <button
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-brand-800 lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="header-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      <MobileNav open={open} close={() => setOpen(false)} />
    </>
  );
};

export default Header;
